import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { getUserDisplayName, getUserInitial, getUserAvatarUrl } from '@/lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface VoterAvatarsProps {
  postId: string;
  maxDisplay?: number;
  size?: 'sm' | 'md';
  className?: string;
}

export function VoterAvatars({
  postId,
  maxDisplay = 5,
  size = 'sm',
  className,
}: VoterAvatarsProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['voters', postId],
    queryFn: () => api.votes.voters(postId, { limit: maxDisplay + 1 }),
  });

  if (isLoading) {
    return (
      <div className={cn('flex -space-x-1.5', className)}>
        {[...Array(3)].map((_, i) => (
          <Skeleton
            key={i}
            className={cn(
              'rounded-full border-2 border-background',
              size === 'sm' ? 'h-6 w-6' : 'h-8 w-8'
            )}
          />
        ))}
      </div>
    );
  }

  if (!data || data.voters.length === 0) {
    return null;
  }

  const { voters, pagination } = data;
  const displayVoters = voters.slice(0, maxDisplay);
  const remaining = pagination.total - maxDisplay;

  const sizeClasses = {
    sm: 'h-6 w-6 text-[10px]',
    md: 'h-8 w-8 text-xs',
  };

  return (
    <TooltipProvider>
      <div className={cn('flex items-center', className)}>
        <div className="flex -space-x-1.5">
          {displayVoters.map((voter, index) => (
            <Tooltip key={voter.id}>
              <TooltipTrigger asChild>
                <Avatar
                  className={cn(
                    sizeClasses[size],
                    'border-2 border-background cursor-default transition-transform hover:scale-110 hover:z-10'
                  )}
                  style={{ zIndex: displayVoters.length - index }}
                >
                  <AvatarImage
                    src={getUserAvatarUrl(voter) || undefined}
                    alt={getUserDisplayName(voter)}
                  />
                  <AvatarFallback className="text-[10px]" colorSeed={voter.id}>
                    {getUserInitial(voter)}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {getUserDisplayName(voter)}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
        {remaining > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'ml-1.5 text-muted-foreground cursor-default',
                  size === 'sm' ? 'text-xs' : 'text-sm'
                )}
              >
                +{remaining}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {remaining} more {remaining === 1 ? 'voter' : 'voters'}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
